import type { ReactNode } from "react";
import { Anchor, Code, Strong, Text } from "@generalapplications/ui";

/**
 * Formats text by converting markdown-style syntax:
 * - Links: [text](url) -> the app's link, or a new tab for another site
 * - Bold: **text** -> bold, in the site's accent ink
 * - Inline code: `code` -> the library's Code
 *
 * @param text - The input string that may contain markdown markers
 * @returns React nodes with formatted elements
 */
export function formatText(text: string): ReactNode {
  if (!text) return "";

  // Regex matching:
  // 1) Link: [label](url)
  // 2) Bold: **text**
  // 3) Inline code: `code`
  const regex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`)/g;

  const elements: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      elements.push(text.slice(lastIndex, match.index));
    }

    if (match[2] !== undefined && match[3] !== undefined) {
      const label = match[2];
      const url = match[3];
      const internal = url.startsWith("/") || url.startsWith("#");
      elements.push(
        <Text key={match.index} as="span" variant="inherit" tone="secondary">
          <Anchor
            href={url}
            tone="inherit"
            weight="medium"
            external={!internal}
          >
            {formatText(label)}
          </Anchor>
        </Text>,
      );
    } else if (match[4] !== undefined) {
      elements.push(
        <Strong key={match.index} tone="secondary">
          {formatText(match[4])}
        </Strong>,
      );
    } else if (match[5] !== undefined) {
      elements.push(<Code key={match.index}>{match[5]}</Code>);
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  return elements.length === 1 ? elements[0] : elements;
}
